import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, lastValueFrom, Observable, Subject} from 'rxjs';
import {AuthService} from 'src/app/shared/services/auth.service';
import {environment} from 'src/environments/environment';
import {getDoc, getDocs, limit, orderBy, query, setDoc, updateDoc, where, Unsubscribe, onSnapshot} from 'firebase/firestore';
import {FirebaseService} from 'src/app/services/firebase.service';


@Injectable({
    providedIn: 'root'
})
export class ChatService {
    private requestToggleChatSubject: Subject<void> = new Subject<void>();
    private openChatSubject: Subject<boolean> = new Subject<false>();
    private minimizedChatSubject: Subject<boolean> = new Subject<false>();
    private unreadCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    /** true si archived_by_hierarchy → ocultar icono en home. */
    private chatArchivedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public chats: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private distributors = new BehaviorSubject<any[]>([]);
    /** True after the first Firestore snapshot of archived chats. */
    private archivedChatsReadySubject = new BehaviorSubject<boolean>(false);

    constructor(
        private auth: AuthService,
        private http: HttpClient,
        private firebaseService: FirebaseService
    ) {
    }

    private getHeaders(): HttpHeaders {
        const token = 'Bearer ' + this.auth.getToken();

        return new HttpHeaders({
            Authorization: token,
            'Content-Type': 'application/json'
        });
    }

    public getHeadersWithoutContentType(): HttpHeaders {
        return new HttpHeaders({
            Authorization: 'Bearer ' + this.auth.getToken()
        });
    }

    async markMessagesAsRead(id: string) {
        const chatRef = await this.firebaseService.getDocument(`chats/${id}`);
        const messagesRef = await this.firebaseService.getCollection(`chats/${id}/messages`);
        const path = 'pdv_read';
        const batch = await this.firebaseService.getWriteBatch();

        const unreadQuery = query(
            messagesRef,
            orderBy('created_at', 'desc'),
            where(path, '==', false),
            limit(500)
        );
        const snapshot = await getDocs(unreadQuery);

        batch.update(chatRef, {['last_message_' + path]: true});
        snapshot.docs.forEach(docSnap => batch.update(docSnap.ref, {[path]: true}));

        await batch.commit();
    }

    async createChatMessage(message: any, file: File | null = null) {
        const chatRef = await this.firebaseService.getDocument(`chats/${message.chat_id}`);
        const messageRef = await this.firebaseService.getDocument(`chats/${message.chat_id}/messages/${message.id}`);

        await setDoc(messageRef, message);
        await updateDoc(chatRef, {
            last_message_text: file != null ? file.name : message.message,
            last_message_date: message.created_at,
            last_message_distribuidor_read: message.distribuidor_read,
            last_message_gpv_read: message.gpv_read,
            last_message_pdv_read: message.pdv_read
        });
    }

    sendChatPushNotification(message: any, file: File | null = null) {
        const body = {
            sender_id: message.sender_id,
            sender_name: message.sender_name,
            sender_type: message.sender_type,
            message: file != null ? file.name : message.message
        };

        this.http.post<any>(environment.msDigoApp + `api/chat/${message.chat_id}/send-push-notification`, body, {headers: this.getHeaders()})
            .subscribe({
                next: (res) => {
                },
                error: (err) => {
                }
            });
    }

    sendChatMessageFile(message: any, file: File) {
        const formData = new FormData();

        formData.append('file', file, file.name);

        return this.http.post<any>(environment.msDigoApp + `api/chat/${message.chat_id}/message/${message.id}/upload-file`, formData, {headers: this.getHeadersWithoutContentType()});
    }

    requestToggleChat() {
        this.requestToggleChatSubject.next();
    }

    get requestToggleChat$(): Observable<void> {
        return this.requestToggleChatSubject.asObservable();
    }

    openChat(value: boolean): void {
        this.openChatSubject.next(value);
    }

    get openChat$(): Observable<boolean> {
        return this.openChatSubject.asObservable();
    }

    minimizedChat(value: boolean): void {
        this.minimizedChatSubject.next(value);
    }

    get minimizedChat$(): Observable<boolean> {
        return this.minimizedChatSubject.asObservable();
    }

    updateUnreadCount(count: number): void {
        this.unreadCountSubject.next(count);
    }

    get unreadCount$(): Observable<number> {
        return this.unreadCountSubject.asObservable();
    }

    setChatArchived(archived: boolean): void {
        this.chatArchivedSubject.next(archived);
    }

    get chatArchived$(): Observable<boolean> {
        return this.chatArchivedSubject.asObservable();
    }

    get isChatArchived(): boolean {
        return this.chatArchivedSubject.getValue();
    }

    getPdvChat(id: any): Observable<any> {
        return this.http.get<any>(environment.msDigoApp + `api/chat/pdv/${id}`, {headers: this.getHeaders()});
    }

    registerFcmToken(body: any): Observable<any> {
        return this.http.post<any>(environment.msDigoApp + `api/chat/register-fcm-token`, body, {headers: this.getHeaders()});
    }

    async removeFcmToken(pdv: any) {
        if (!pdv?.info?.idpdv) {
            return;
        }
        const token = await this.firebaseService.registerMessagingToken();
        if (!token) {
            return;
        }

        const body = {
            id_pdv: pdv.info.idpdv,
            token: token
        };

        try {
            await lastValueFrom(
                this.http.post<any>(environment.msDigoApp + `api/chat/remove-fcm-token`, body, {headers: this.getHeaders()})
            );
        } catch {
            // No bloquear el logout si el backend falla
        }
    }

    createChatRemote(id_pdv: any): Observable<any> {
        return this.http.post<any>(environment.msDigoApp + `api/chat/create`, {id_pdv}, {headers: this.getHeaders()});
    }

    async deleteMessage(chatId: string, messageId: string) {
        await this.firebaseService.deleteDocument(`chats/${chatId}/messages/${messageId}`);

        let data: any;
        const chatRef = await this.firebaseService.getDocument(`chats/${chatId}`);
        const lastMessage = await this.firebaseService.getLastMessage(chatId);

        if (lastMessage != null) {
            data = {
                last_message_text: lastMessage.message,
                last_message_date: lastMessage.created_at,
                last_message_distribuidor_read: lastMessage.distribuidor_read,
                last_message_gpv_read: lastMessage.gpv_read,
                last_message_pdv_read: lastMessage.pdv_read,
            };
        } else {
            const chat = await getDoc(chatRef);

            data = {
                last_message_text: '',
                last_message_date: chat.data()?.['created_at'],
                last_message_distribuidor_read: null,
                last_message_gpv_read: null,
                last_message_pdv_read: null,
            };
        }

        await updateDoc(chatRef, data);
    }

    base64UrlDecode(input: string): string {
        // convertir base64url a base64
        input = input.replace(/-/g, '+').replace(/_/g, '/');
        // añadir padding si hace falta
        const pad = input.length % 4;
        if (pad) {
            input += '='.repeat(4 - pad);
        }
        // atob decodifica base64 a string
        return decodeURIComponent(
            Array.prototype.map
                .call(atob(input), (c: string) => {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join('')
        );
    }

    decodeJwt(token: string): any | null {
        if (!token) {
            return null;
        }
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        } // no es un JWT típico
        try {
            const payload = this.base64UrlDecode(parts[1]);
            return JSON.parse(payload);
        } catch (e) {
            console.error('Invalid token decode', e);
            return null;
        }
    }

    isUserImpersonated(jwt: string): boolean {
        const payload = this.decodeJwt(jwt);

        console.log(payload);

        if (payload) {
            return payload.data.originalUserId != null;
        }

        return false;
    }

    onNewChat(pdv_id: any, handler: (chat: any) => void) {
        let unsubscribe: Unsubscribe | null = null;

        this.firebaseService.getNewChat(pdv_id, (snapshot) => {
            if (!snapshot.empty) {
                handler({id: snapshot.docs[0].id, ...snapshot.docs[0].data()});
                unsubscribe?.();
            }
        }).then((sub) => unsubscribe = sub);
    }

    async getArchivedChatsFromFilestore(pdvId: any) {
        let constraints: any[] = [];

        if(pdvId == null) {
            return;
        }

        if (pdvId) {
            constraints.push(where('id_pdv', "==", parseInt(pdvId)));
            constraints.push(where('archived', "==", true));
        }

        constraints.push(orderBy("last_message_date", "desc"))

        const chatCollection = await this.firebaseService.getCollection("chats");
        const chatQuery = query(chatCollection, ...constraints);

        onSnapshot(chatQuery, (snapshot) => {
            const chats = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            })).filter((chat:any)  => chat.last_message_text);

            this.chats.next(chats);
            // Evitar empty flash: no marcar ready en snapshot vacío solo de caché local
            if (!snapshot.metadata.fromCache || chats.length > 0) {
                this.archivedChatsReadySubject.next(true);
            }
        }, (error) => {
            console.error("getArchivedChats error:", error);
            this.archivedChatsReadySubject.next(true);
        });
    }


    async getArchivedChatByIdFromFilestore(chatId: string) {
        return await this.firebaseService.getMessages(chatId);
    }

    get archivedChats$(): Observable<any[]> {
        return this.chats.asObservable();
    }

    get archivedChatsReady$(): Observable<boolean> {
        return this.archivedChatsReadySubject.asObservable();
    }

    get archivedChatsReady(): boolean {
        return this.archivedChatsReadySubject.value;
    }

      public normalizeMessages(raw: any[], user: any): Array<any> {
        const sorted = (raw || []).slice().sort((a: any, b: any) => this.toMillis(a.created_at) - this.toMillis(b.created_at));
        const todayMs = Date.now();
        const result: Array<any> = [];
        let lastSeparatorDayMs: number | null = null;

        for (const m of sorted) {
            const createdMs = this.toMillis(m.created_at) || Date.now();

            // Insertar separador por fecha si cambia el día
            const currentDayStart = new Date(createdMs);
            currentDayStart.setHours(0, 0, 0, 0);
            const currentDayMs = currentDayStart.getTime();
            if (lastSeparatorDayMs === null || currentDayMs !== lastSeparatorDayMs) {
                const label = this.isSameDay(createdMs, todayMs) ? 'HOY' : new Date(createdMs).toLocaleDateString('es-ES');
                result.push({separator: label});
                lastSeparatorDayMs = currentDayMs;
            }

            result.push({
                id: m.id,
                name: (m.sender_type == "distribuidor" ? this.getDistributors().find(d => d.id_distribuidor == m.sender_id)?.razon_social : m.sender_name) ?? 'Usuario',
                nameClass: this.pickClass(m.sender_type ?? '', user),
                time: this.formatTime(createdMs),
                text: m.message ?? '',
                file_url: m.file_url,
                file_name: m.file_name,
                file_status: m.file_status,
                file_is_image: /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(m.file_url),
                type: m.sender_type ?? '',
                created_at: m.created_at.toDate(),
                archived: m.archived,
                distribuidor_read: m.distribuidor_read,
                gpv_read: m.gpv_read,
                pdv_read: m.pdv_read,
                sender_id: m.sender_id,
                distributor_name: this.getDistributors().find(d => d.id_distribuidor == m.sender_id)?.razon_social ?? null
            });
        }

        return result;
    }

    private toMillis(ts: any): number {
        if (!ts) {
            return 0;
        }
        if (typeof ts === 'number') {
            return ts;
        }
        if (ts.seconds != null) {
            return (ts.seconds * 1000) + Math.floor((ts.nanoseconds || 0) / 1e6);
        }
        const d = new Date(ts);
        return isNaN(d.getTime()) ? 0 : d.getTime();
    }

    private isSameDay(aMs: number, bMs: number): boolean {
        const a = new Date(aMs);
        const b = new Date(bMs);
        return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    private pickClass(senderType: string, user: any): string {
        const type = (senderType || '').toString().toLowerCase();

        if (type == 'distribuidor') {
            return 'color-green';
        }

        if (type == 'gpv') {
            return 'color-blue';
        }

        if (type == 'pdv') {
            return 'color-orange';
        }

        return 'color-black';
    }

    private formatTime(ms: number): string {
        return new Date(ms).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
    }


    getDistributors() {
        return this.distributors.value || [];
    }

    setDistributors(newDistributors: any[]) {
        const current = this.getDistributors();

        const merged = [
            ...current,
            ...newDistributors.filter(
            d => !current.some(c => c.id_distribuidor === d.id_distribuidor)
            )
        ];

        this.distributors.next(merged);
    }

    
   public normalizeThread(raw: any): any {
        const list: any[] = raw ? [raw] : [];

        const sorted = (raw || []).slice().sort((a: any, b: any) => this.toMillis(a.created_at) - this.toMillis(b.created_at));

        const result: Array<any> = [];

        for (const m of sorted) {
            const createdMs = this.toMillis(m.created_at) || Date.now();
            const lastMessage = this.toMillis(m.last_message_date) || Date.now();

            // Inicio del día
            const currentDayStart = new Date(createdMs);
            currentDayStart.setHours(0, 0, 0, 0);

            result.push({
                id: m.id,
                archived: m.archived,
                created_at: m.created_at?.toDate?.() ?? new Date(createdMs),
                distributor_name: this.getDistributors().find(d => d.id_distribuidor == m.id_distribuidor)?.razon_social ?? null,
                last_message_date: this.formatTime(lastMessage),
                last_message_text: m.last_message_text
            });
        }

        return result;
    }



}
